import React, { FC, useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';

import {
  FEATURE_FLAG_MENU_NOTIFICATIONS,
  FEATURE_FLAG_PLANS_V2,
  FEATURE_FLAG_TOOL_COLOR_CODE,
  FEATURE_FLAG_TOOL_CLOUDS,
  FEATURE_FLAG_TOOL_SAND_THERAPY,
} from '../../constants/featureFlags';

import { getNotificationsForMenuItem, parseMenuNotificationsPayload } from './menu-notification-data';
import { MenuNotificationCompact, MenuUnreadCountTrailing } from './menu-notification-trailing';
import { useSupportTicketUnreadCount } from '../../features/support/use-support-ticket-unread-count';
import { useSupportAiBotFeatureFlag } from '../../features/support/useSupportAiBotFeatureFlag';
import { CloseButton, Heading } from '../../shared/ui';

interface MenuMobileProps {
  openMenu: boolean;
  onCloseMenu: () => void;
}

const menuItems = [
  { to: '/profile', label: 'Профиль' },
  { to: '/consultations', label: 'Создать встречу', extraMatch: ['settings'] },
  { to: '/journal', label: 'Терапевтический журнал' },
];

const toolItems = [
  { to: '/tools/ai-assistants', label: 'ИИ Ассистенты' },
  { to: '/tools/emotion-detector', label: 'Детектор эмоций' },
  { to: '/tools/metaphorical-cards', label: 'Метафорические карты' },
  { to: '/tools/question', label: 'Подкинь вопрос' },
  { to: '/tools/dpdg', label: 'ДПДГ онлайн' },
  { to: '/tools/life-balance-wheel', label: 'Колесо баланса' },
  { to: '/tools/ai-grow', label: 'GROW AI' },
  { to: '/tools/color-code', label: 'Цветовой код' },
  { to: '/tools/clouds', label: 'Облака' },
  { to: '/tools/sand-therapy', label: 'Песочная терапия' },
  { to: '/tools/arrangements', label: 'Расстановки' },
];

const bottomItems = [
  { to: '/tariffs', label: 'Тарифы и бонусы' },
  { to: '/info', label: 'Информация' },
  { to: '/referal-management', label: 'Ваш ключ' },
  { to: '/support-tickets', label: 'Мои обращения' },
];

export const MenuMobile: FC<MenuMobileProps> = ({ openMenu, onCloseMenu }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const [, path] = pathname.split('/');
  const isColorCodeEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_COLOR_CODE);
  const isCloudsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_CLOUDS);
  const isSandTherapyEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_SAND_THERAPY);
  const isPlansV2Enabled = useFeatureFlagEnabled(FEATURE_FLAG_PLANS_V2);
  const { enabled: isSupportAiBotEnabled } = useSupportAiBotFeatureFlag();
  const unreadSupportMessages = useSupportTicketUnreadCount(isSupportAiBotEnabled);
  const menuNotificationsPayload = useFeatureFlagPayload(FEATURE_FLAG_MENU_NOTIFICATIONS);
  const visibleToolItems = useMemo(
    () =>
      toolItems.filter(
        (item) =>
          (item.to !== '/tools/color-code' || isColorCodeEnabled) &&
          (item.to !== '/tools/clouds' || isCloudsEnabled) &&
          (item.to !== '/tools/sand-therapy' || isSandTherapyEnabled) &&
          (item.to !== '/tools/arrangements' || isSandTherapyEnabled),
      ),
    [isColorCodeEnabled, isCloudsEnabled, isSandTherapyEnabled],
  );
  const menuNotifications = useMemo(
    () => parseMenuNotificationsPayload(menuNotificationsPayload),
    [menuNotificationsPayload],
  );
  const toolsNotifications = useMemo(
    () => getNotificationsForMenuItem('tools', menuNotifications),
    [menuNotifications],
  );
  const tariffsNotifications = useMemo(
    () => getNotificationsForMenuItem('tariffs', menuNotifications),
    [menuNotifications],
  );
  const infoNotifications = useMemo(() => getNotificationsForMenuItem('info', menuNotifications), [menuNotifications]);
  const visibleBottomItems = useMemo(() => {
    const items = isPlansV2Enabled
      ? [{ to: '/subscriptions', label: 'Мои подписки' }, ...bottomItems.filter((item) => item.to !== '/tariffs')]
      : bottomItems;

    return items.filter((item) => item.to !== '/support-tickets' || isSupportAiBotEnabled);
  }, [isPlansV2Enabled, isSupportAiBotEnabled]);
  const isActiveWithExtra = (extraMatch?: string[]) => extraMatch?.includes(path) ?? false;

  useEffect(() => {
    if (!openMenu) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseMenu();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.classList.add('hidden_scroll');
    window.addEventListener('keydown', handleKeyDown);
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      document.body.classList.remove('hidden_scroll');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCloseMenu, openMenu]);

  if (!openMenu) return null;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-modal bg-white lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-menu-title"
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex min-h-16 items-center justify-between border-b border-line px-4 sm:px-6">
          <Heading size="md" id="mobile-menu-title">
            Меню
          </Heading>
          <CloseButton label="Закрыть меню" className="m-1.5" ref={closeButtonRef} onClick={onCloseMenu} />
        </div>

        <nav
          id="mobile-navigation"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 text-ui text-ink sm:px-6"
        >
          <div className="flex flex-col gap-1 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {menuItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onCloseMenu}
                className={({ isActive }) =>
                  classNames(
                    'flex min-h-12 w-full items-center rounded-control px-4 py-3 font-medium transition-colors',
                    (isActive || isActiveWithExtra(item.extraMatch)) && 'bg-primary text-white',
                  )
                }
              >
                {({ isActive }) => (
                  <span className="flex w-full items-center justify-between gap-2">
                    <span>{item.label}</span>
                  </span>
                )}
              </NavLink>
            ))}

            <div className="my-1 flex flex-col border-t border-surface-muted pt-2">
              <NavLink
                to="/tools"
                onClick={onCloseMenu}
                className={({ isActive }) =>
                  classNames(
                    'flex min-h-11 w-full items-center justify-between gap-2 rounded-control px-4 py-2 text-ui-xs uppercase tracking-wide transition-colors',
                    isActive ? 'bg-primary text-white' : 'text-ink-subtle',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span>Инструменты</span>
                    <MenuNotificationCompact variant="bell" notifications={toolsNotifications} isActive={isActive} />
                  </>
                )}
              </NavLink>
              <div className="mt-[-2px] flex flex-col gap-1 rounded-b-card bg-surface-muted px-2 py-2">
                {visibleToolItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onCloseMenu}
                    className={({ isActive }) =>
                      classNames(
                        'flex min-h-11 w-full items-center rounded-control px-4 py-2 text-ui transition-colors',
                        isActive ? 'bg-primary text-white' : 'text-ink hover:bg-white/70',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>

            {visibleBottomItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onCloseMenu}
                className={({ isActive }) =>
                  classNames(
                    'flex min-h-12 w-full items-center rounded-control px-4 py-3 font-medium transition-colors',
                    isActive && 'bg-primary text-white',
                  )
                }
              >
                {({ isActive }) => (
                  <span className="flex w-full items-center justify-between gap-2">
                    <span>{item.label}</span>
                    {item.to === '/tariffs' && (
                      <MenuNotificationCompact
                        variant="typed"
                        notifications={tariffsNotifications}
                        isActive={isActive}
                      />
                    )}
                    {item.to === '/info' && (
                      <MenuNotificationCompact variant="typed" notifications={infoNotifications} isActive={isActive} />
                    )}
                    {item.to === '/support-tickets' && (
                      <MenuUnreadCountTrailing count={unreadSupportMessages} isActive={isActive} />
                    )}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default MenuMobile;
